import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Copy, Check, Terminal } from 'lucide-react'
import type { Device } from '@/types'

interface InstallCommandDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  device: Device
}

const INSTALL_SCRIPT_URL = `${
  import.meta.env.VITE_API_BASE_URL || 'https://api.auvers.ai'
}/installScript`

function formatApiKey(apiKey: string): string {
  // Format as XXXX-XXXX-XXXX for 12-char keys, or show as-is for legacy keys
  if (apiKey.length === 12) {
    return `${apiKey.slice(0, 4)}-${apiKey.slice(4, 8)}-${apiKey.slice(8, 12)}`
  }
  return apiKey
}

export function InstallCommandDialog({ open, onOpenChange, device }: InstallCommandDialogProps) {
  const { t } = useTranslation()
  const [copiedCommand, setCopiedCommand] = useState(false)
  const [copiedKey, setCopiedKey] = useState(false)

  const formattedKey = formatApiKey(device.apiKey || '')
  const installCommand = `curl -fsSL ${INSTALL_SCRIPT_URL} | sudo API_KEY=${formattedKey} bash`

  const handleCopyCommand = async () => {
    await navigator.clipboard.writeText(installCommand)
    setCopiedCommand(true)
    setTimeout(() => setCopiedCommand(false), 2000)
  }

  const handleCopyKey = async () => {
    await navigator.clipboard.writeText(formattedKey)
    setCopiedKey(true)
    setTimeout(() => setCopiedKey(false), 2000)
  }

  const handleClose = () => {
    setCopiedCommand(false)
    setCopiedKey(false)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Terminal className="h-5 w-5" />
            {t('install.title')}
          </DialogTitle>
          <DialogDescription>
            {t('install.description', { deviceName: device.name })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* API Key */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-ink">{t('install.apiKey')}</label>
            <div className="flex gap-2">
              <code className="min-w-0 flex-1 bg-paper-2 rounded-md px-3 py-2 text-sm font-mono tracking-wider break-all">
                {formattedKey}
              </code>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="shrink-0"
                onClick={handleCopyKey}
              >
                {copiedKey ? <Check className="h-4 w-4 text-gold" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* Install Command */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-ink">{t('install.command')}</label>
            <div className="flex gap-2">
              <code className="min-w-0 flex-1 bg-gray-900 text-green-400 rounded-md px-3 py-2 text-xs font-mono break-all leading-relaxed">
                {installCommand}
              </code>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="shrink-0"
                onClick={handleCopyCommand}
              >
                {copiedCommand ? <Check className="h-4 w-4 text-gold" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* Instructions */}
          <div className="text-sm text-muted-foreground space-y-1">
            <p>{t('install.runOnDevice')}</p>
            <p className="text-xs">{t('install.manualHint')}</p>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={handleClose}>{t('common.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
